import {ConfigMatrix} from './portfolio-data-context.service';
import {PortfolioSegmentDataContext} from './portfolio-segment-data-context.service';
import {AccountData, ClassificationTierTarget, Portfolio, Target, TieringStats} from '../../api-client-kt/src';
import {
    FormulaEvaluateResponse,
    FormulaEvaluateTableColumn,
    FormulaEvaluateTableInput,
    type FormulaEvaluateValue
} from '../../api-client-py/src';
import {DataDictionaryService} from './data-dictionary';
import {JsonDataType, JsonHelper} from '../lib/json';
import {AppConstants} from '../app.constants';
import {AppDataFormulaService} from './app-data-formula.service';
import {AppComponent} from '../app.component';
import {AccountDataWithChecks, CheckResults, CheckSeverity, CheckUtils} from './app-data-checks';
import {AccountSegmentStatisticsCalculator, PortfolioSegmentStatistics} from './portfolio-segment-statistics';
import {uniq} from 'es-toolkit';
import {ClassificationTierTargetWithName} from "./portfolio-segment-classification-scheme";
import {signal} from '@angular/core';
import {PfTargetUwySummaryRow, PfValidationCheckItem} from './portfolio-segment-report-data';
import {ArrayUtils} from '../lib/arrays';
import {NullablePrimitive} from '../lib/stdlib';
import {AuthService} from '../security/auth.service';

interface FormulaEvaluateTableColumnNonNullJsonType {
    name: string,
    jsonType: JsonDataType
}

export interface AccountFormulaEvalOptions {
    accounts?: AccountData[];
    parameters?: Record<string, NullablePrimitive>;
    abortSignal?: AbortSignal;
}

export class FormulaTableAccessor {
    private readonly indexByColName: Map<any, number> = new Map<any, number>();

    constructor(private table: FormulaEvaluateTableInput, private keyColName: string) {
    }

    getIndex(columnName: string): number {
        const cached = this.indexByColName.get(columnName);
        if (cached !== undefined) {
            return cached;
        } else {
            const columnIdx = this.table.columns.findIndex(c => c.name === columnName);
            if (columnIdx === -1) throw new Error(`Column ${columnName} not found in table ${this.table.name}`);
            this.indexByColName.set(columnName, columnIdx);
            return columnIdx;
        }
    }

    getValue(key: string, columnName: string): any {
        const keyIdx = this.getIndex(this.keyColName);
        const valIdx = this.getIndex(columnName);
        const index = this.table.data.findIndex(r => r[keyIdx] === key);
        return this.table.data[index][valIdx];
    }

    getRowValue(row: any[], columnName: string): any {
        const valIdx = this.getIndex(columnName);
        return row[valIdx];
    }

    getAllRowsAsObjects(): any[] {
        return this.table.data.map(row => Object.fromEntries(
            this.table.columns.map((col, idx) => [col.name, row[idx]]))
        );
    }
}

/**
 * The PortfolioSegmentWorkbook class is an equivalent of the Excel workbook used for PIL files.
 * It performs calculations, provides methods for accessing portfolio segment data, including account data
 * performing data evaluations through formula processing, and handling tier and account data.
 * This class interacts with portfolio context, accounts, and tier information within
 * a specified data context to provide comprehensive portfolio segmentation management.
 *
 * Key Responsibilities:
 * - Manages data related to portfolio segments, including accounts and classification tiers.
 * - Provides methods to assert readiness by ensuring all resources in the data context are loaded.
 * - Facilitates configurable access to matrix values based on keys.
 * - Integrates formula evaluation for account and tier data calculations.
 * - Handles data synchronization between received results and the workbook's data context.
 *
 */
export class PortfolioSegmentWorkbook {

    static NonePortfolio: Portfolio = {
        id: undefined,
        period: undefined,
        phase: 'Planning',
        uwyear: -1,
        description: 'No portfolio is active',
        status: 'Draft',
    }

    static readonly AccountAttributesToSend: string[] = [
        'id', 'crmSubmissionId', 'uwy', 'inceptionDate', 'expiryDate',
        'expiringCurrency', 'gwp', 'gnwpEur',
        'renewalCurrency', 'renewalGwp', 'renewalGnwp',
        'renewableFlag', 'keyAccount', 'prevPilStatus', 'manualPilStatus', 'rationaleChange',
        'targetRateChangeKeyAccounts', 'retentionRateKeyAccounts',
        'renewalStatus', 'methodology', 'manualOverride',
        'aptp', 'renewalAptp',
        'expiringShare', 'renewalShare',
        'expiringExposure', 'renewalExposure',
        'expiringDeductible', 'renewalDeductible',
        'expiringLimit', 'renewalLimit',
        'renewalInceptionDate', 'renewalExpiryDate',
        'hazardGrade', 'repricingFacilityErc',
        'largeFrequency', 'largeLossMargin', 'adjustedVolatility',
        'manualErc'
    ];

    static readonly AccountAttributesToReceive: string[] = [
        'id', 'gnwpEur', 'renewalGnwp', 'portfolioWeight', 'bucketWeight', 'renewalRunningDays',
        'gwpEur', 'renewalGwpEur', 'renewalGnwpEur', 'scoreAts',
        'pricing_adequacy', 'pricing_adequacy_adjusted', 'adequacy_percentile',
        'adequacy_wpercentile', 'adequacy_score', 'adequacy_score_rescaled',
        'calculatedPilStatus', 'finalPilStatus', 'targetRateChange', 'new_target_rate_change',
        "retainedGnwp", "expiring_gnwp_earned_in_uwy", "expiring_gnwp_earned_in_uwy_renewal",
        "purePremiumChange", "asIfPremiumExpired", "riskFactors", 'effectiveRateChange', 'retentionRate'
    ];

    private static AccountColumnsToSend: FormulaEvaluateTableColumn[] =
        PortfolioSegmentWorkbook.AccountAttributesToSend.map(PortfolioSegmentWorkbook.accountAttrToColumn);

    private static AccountColumnsToReceive: FormulaEvaluateTableColumn[] =
        PortfolioSegmentWorkbook.AccountAttributesToReceive.map(PortfolioSegmentWorkbook.accountAttrToColumn);

    private static ClassificationTierTargetColumns: FormulaEvaluateTableColumnNonNullJsonType[] = [
        {name: 'id', jsonType: JsonDataType.String},
        {name: 'tierName', jsonType: JsonDataType.String},
        {name: 'fromValue', jsonType: JsonDataType.Number},
        {name: 'toValue', jsonType: JsonDataType.Number},
        {name: 'targetRetentionRate', jsonType: JsonDataType.Number},
        {name: 'targetRateChange', jsonType: JsonDataType.Number},
        {name: 'targetRateChangeMin', jsonType: JsonDataType.Number},
        {name: 'targetRateChangeMax', jsonType: JsonDataType.Number},
        {name: 'targetRateChangeAdjustment', jsonType: JsonDataType.Number},
        {name: 'targetRateChangeRemaining', jsonType: JsonDataType.Number},
        {name: 'targetRetentionRateRemaining', jsonType: JsonDataType.Number},
        {name: 'classificationTierId', jsonType: JsonDataType.String},
        {name: 'classificationTierKey', jsonType: JsonDataType.String},
    ];

    uwySummary = signal<PfTargetUwySummaryRow[]>([]);
    checksSummary = signal<PfValidationCheckItem[]>([]);
    tieringStats = signal<TieringStats[]>([]);
    rateChangeInterpolationTable = signal<any[]>([]);

    constructor(private pfSegDataCtx: PortfolioSegmentDataContext, private appDataFormulaService: AppDataFormulaService) {
    }

    get uwy(): number {
        return this.pfSegDataCtx.pfContext.portfolio.value()?.uwyear ?? -1;
    }

    configMatrix<V = number>(matrixKey: string): ConfigMatrix<V> | undefined {
        return this.pfSegDataCtx.pfContext.configMatrices.value()?.[matrixKey];
    }

    configMatrixValue<V = number>(matrixKey: string): V | undefined {
        const matrix = this.configMatrix(matrixKey);
        const lobKey = this.pfSegDataCtx.activeSegment.value()?.lobKey;
        const officeKey = this.pfSegDataCtx.activeSegment.value()?.officeKey;
        if (!matrix || !lobKey || !officeKey) {
            return undefined as V;
        }
        return matrix[officeKey]?.[lobKey] as V;
    }

    configMatrixAccessor<V = number>(): ((matrixKey: string) => V | undefined) {
        const lobKey = this.pfSegDataCtx.activeSegment.value()?.lobKey;
        const officeKey = this.pfSegDataCtx.activeSegment.value()?.officeKey;
        if (!lobKey || !officeKey) {
            return (matrixKey) => undefined;
        } else {
            return (matrixKey) => this.configMatrixValue<V>(matrixKey);
        }
    }

    async evaluateAccountFormulasAndChecks(options?: AccountFormulaEvalOptions): Promise<FormulaEvaluateResponse | undefined> {
        const kpiAttributes = this.pfSegDataCtx.segmentKpis.value()?.map(k => k.attribute) ?? [];
        const kpiWeightsAndPercentilesAttr = PortfolioSegmentWorkbook.kpisToPercentileAndWeightAttributes(kpiAttributes);

        const attrNamesToSend = uniq([...PortfolioSegmentWorkbook.AccountAttributesToSend, ...kpiAttributes]);
        const attrNamesToReceive = uniq([...PortfolioSegmentWorkbook.AccountAttributesToReceive, ...kpiWeightsAndPercentilesAttr]);

        const attrsToSend = attrNamesToSend.map(PortfolioSegmentWorkbook.accountAttrToColumn);
        const attrsToReceive = attrNamesToReceive.map(PortfolioSegmentWorkbook.accountAttrToColumn);

        const accounts = options?.accounts ?? this.pfSegDataCtx.getAccountData()
        //.sort((a, b) => (b.gnwpEur ?? 0) - (a.gnwpEur ?? 0)); // uncomment to create a definite order

        if (accounts.length === 0) {
            return undefined;
        }

        const accountToArrayMapper = (row: any) => JsonHelper.objAttrsAsJsonArray(row, attrsToSend);
        const accountsTableData = accounts.map(accountToArrayMapper);

        const tiers = this.pfSegDataCtx.classificationTierSchema.value()?.tierTargets ?? [];
        const tiersAttrs = PortfolioSegmentWorkbook.ClassificationTierTargetColumns;
        const tierToArrayMapper = (row: ClassificationTierTarget) => JsonHelper.objAttrsAsJsonArray(row, tiersAttrs);
        const tiersTableData = tiers.map(tierToArrayMapper);

        const tables: FormulaEvaluateTableInput[] = [
            {name: AppConstants.PYFORMULA_ACCOUNTS_TABLE_NAME, columns: attrsToSend, data: accountsTableData},
            {name: AppConstants.PYFORMULA_TIERS_TABLE_NAME, columns: tiersAttrs, data: tiersTableData},
        ];

        const parameters = options?.parameters ?? {};
        const parameterValues = Object.entries(parameters)
            .filter(([key, value]) => value != undefined)
            .map(([key, value]) => (
                {
                    name: key,
                    jsonType: JsonHelper.jsonTypeFromValue(value),
                    value
                } as FormulaEvaluateValue));

        const values: FormulaEvaluateValue[] = [
            {name: AppConstants.PYFORMULA_SEGMENT_ID_NAME, jsonType: "string", value: this.pfSegDataCtx.segmentId()!},
            ...parameterValues
        ];

        const tablesToReceive = {
            [AppConstants.PYFORMULA_ACCOUNTS_TABLE_NAME]: attrsToReceive,
            [AppConstants.PYFORMULA_ACCOUNT_CHECKS_DICTIONARY_TABLE_NAME]: [],
            [AppConstants.PYFORMULA_ACCOUNT_CHECKS_TABLE_NAME]: [],
            [AppConstants.PYFORMULA_TRC_TABLE_NAME]: [],
            [AppConstants.PYFORMULA_TRC_INTERPOLATION_TABLE_NAME]: [],
            [AppConstants.PYFORMULA_PORTFOLIO_SUMMARY_TABLE_NAME]: [],
            [AppConstants.PYFORMULA_TIERING_STATS_TABLE_NAME]: [],
            [AppConstants.PYFORMULA_TIERING_BE_TABLE_NAME]: []
        };

        const methods = [AppConstants.PYFORMULA_ACCOUNT_DATA_METHOD, AppConstants.PYFORMULA_ACCOUNT_DATA_CHECKS_METHOD];

        const result = await this.appDataFormulaService.evaluateFormula(methods, values, tables, {}, tablesToReceive, options?.abortSignal);
        if (!result) return;

        PortfolioSegmentWorkbook.showErrorToasts(result);
        this.mergeAccountDataResults(result, accounts, attrsToReceive);
        this.readChecksSummary(result);
        this.mergeTieringResults(result, tiers);
        this.readSummaries(result, tiers);
        this.readTieringStats(result);

        this.pfSegDataCtx.updateClassificationTierSchema(tiers);

        // notify the collection view and attached UI components that the data has changed
        this.pfSegDataCtx.accountsCollectionView.value().refresh();

        return result;
    }

    private static getTableByName(tables: FormulaEvaluateTableInput[], name: string): FormulaEvaluateTableInput {
        const table = tables.find(t => t.name === name);
        if (!table) {
            throw new Error(`Table ${name} not found`);
        }
        return table;
    }

    private mergeAccountDataResults(evalResponse: FormulaEvaluateResponse, accountsInCv: AccountData[], attrs: FormulaEvaluateTableColumnNonNullJsonType[]): void {

        const tableByName = PortfolioSegmentWorkbook.getTableByName.bind(undefined, evalResponse.tables!);

        const accountsTable = tableByName(AppConstants.PYFORMULA_ACCOUNTS_TABLE_NAME);
        const accountData = accountsTable.data;

        // collection view may have been disposed by this time from async operations
        if (accountsInCv.length === 0) {
            return undefined;
        }

        const accountsMap = new Map<string, AccountData>(accountsInCv.map(a => [a.id!, a]));

        // construct an ID extractor function
        const idIndex = accountsTable.columns.findIndex(c => c.name === 'id');
        const accountId = (row: any) => row[idIndex] as string;

        for (let i = 0; i < accountData.length; i++) {
            const evaluatedAccount = accountData[i];
            const account = accountsMap.get(accountId(evaluatedAccount));

            if (account) {
                for (let j = 0; j < attrs.length; j++) {
                    const attr = attrs[j];
                    const n = attr.name as keyof AccountData;
                    account[n] = JsonHelper.convertValueFromJson(evaluatedAccount[j], attr.jsonType as JsonDataType);
                }
            }
        }

        // validations from the service are returned grouped by check id.
        // we need to map them to the accounts in the collection view
        accountsInCv.forEach(account => {
            (account as any).errorLevel = undefined;
            account.metadataChecks = [];
        });

        const checksDictTable = tableByName(AppConstants.PYFORMULA_ACCOUNT_CHECKS_DICTIONARY_TABLE_NAME);
        const checksTable = tableByName(AppConstants.PYFORMULA_ACCOUNT_CHECKS_TABLE_NAME);

        const checkResultParser = new CheckResults(checksDictTable);
        checkResultParser.checks.forEach(check => {
            const checkId = check.id;
            const checkMatchesIdx = checksTable.columns.findIndex(c => c.name === checkId);

            checksTable.data.forEach(id => {
                const accountId = id[checkMatchesIdx] as string;
                const account = accountsMap.get(accountId) as AccountDataWithChecks;
                if (account) {
                    account.errorLevel = CheckUtils.max(check.severity, account.errorLevel);
                    if (!account.metadataChecks) {
                        account.metadataChecks = [];
                    }
                    (account.metadataChecks as any[]).push({
                        id: check.id,
                        name: check.name,
                        severity: check.severity,
                        fields: check.fields
                    });
                }
            });
        });
    }

    private mergeTieringResults(evalResponse: FormulaEvaluateResponse, tiers: ClassificationTierTargetWithName[]): void {
        try {
            const tables = evalResponse.tables!;
            const table = PortfolioSegmentWorkbook.getTableByName(tables, AppConstants.PYFORMULA_TRC_INTERPOLATION_TABLE_NAME);
            const trciTable = new FormulaTableAccessor(table, 'index');

            const allRowsAsObjects = trciTable.getAllRowsAsObjects();

            tiers.forEach(tier => {
                //tier.targetRateChangeAdjustment = trciTable.getValue(tier.tierName, 'adjusting_factor') as number;
                const match = allRowsAsObjects.find((row: any) => row.index === tier.tierName);
                if (match) {
                    match.targetRateChangeAdjustment = tier.targetRateChangeAdjustment;
                }
            });

            this.rateChangeInterpolationTable.set(allRowsAsObjects);
        } catch (e) {
            console.error(e);
        }
    }

    private static showErrorToasts(result: FormulaEvaluateResponse | undefined) {
        if (result == null) return;

        const errorsString = result?.debugInfo?.messages
            ?.filter(it => it.status === 'error')
            ?.map(it => it.message)
            ?.join('\n');

        if (errorsString) {
            const methodsString = result?.methods?.join(':');
            AppComponent.messageToast.show({ title: `Errors occured on formula evaluation '${methodsString}'`, message: errorsString });
        }
    }

    private readSummaries(evalResponse: FormulaEvaluateResponse, tiers: ClassificationTierTargetWithName[]): void {
        const tables = evalResponse.tables!;
        const tableRef = PortfolioSegmentWorkbook.getTableByName(tables, AppConstants.PYFORMULA_PORTFOLIO_SUMMARY_TABLE_NAME);
        const table = new FormulaTableAccessor(tableRef, 'index');

        const uwySummary: PfTargetUwySummaryRow[] = tableRef.data.map(row => ({
                bucket: table.getRowValue(row, 'index'),
                count: table.getRowValue(row, 'policy_count'),
                countPercent: table.getRowValue(row, 'policy_count_pct'),

                // ------------------------------------------------------------------
                // expiring portfolio GNWP and NLR
                gnwpEur: table.getRowValue(row, 'exp_gnwp'),
                nlr: table.getRowValue(row, 'nlr'),
                indicativeTargetRateChange: table.getRowValue(row, 'indicative_target_rate_change'),

                // plan for renewal (also 'original portfolio targets')
                targetRateChange: table.getRowValue(row, 'targetRateChange'),
                targetRetentionRate: table.getRowValue(row, 'targetRetentionRate'),

                // resulting plan for renewal in terms of GNWP and NLR
                renewalGnwpEur: table.getRowValue(row, 'renewal_gnwp'),
                renewalClaimsEur: table.getRowValue(row, 'claims'),
                renewalNlr: table.getRowValue(row, 'renewal_nlr'),

                // ------------------------------------------------------------------------------------------
                // tracking - in terms of expiring GNWP - what has been renewed and lost and what's remaining
                expGnwpEurRenewed: table.getRowValue(row, 'exp_gnwp_eur_renewed'),
                expGnwpEurLostCancelled: table.getRowValue(row, 'exp_gnwp_eur_lost'),
                expGnwpEurRemaining: table.getRowValue(row, 'exp_gnwp_eur_remaining'),

                expNlrRenewed: table.getRowValue(row, 'exp_nlr_renewed'),
                expNlrLostCancelled: table.getRowValue(row, 'exp_nlr_lost'),
                expNlrRemaining: table.getRowValue(row, 'exp_nlr_remaining'),

                // ------------------------------------------------------------------------------------------
                // split up the renewal plan into renewal and renewal-new (for the next year)

                gnwpEurRenewed: table.getRowValue(row, 'renewal_gnwp_eur_renewed'),
                gnwpEurRenewedUwy: table.getRowValue(row, 'renewal_gnwp_eur_uwy_renewed'),
                gnwpEurRemaining: table.getRowValue(row, 'gnwp_eur_remaining'),
                gnwpEurFinalRemaining: table.getRowValue(row, 'gnwp_eur_final_remaining'),
                gnwpEurNewTotal: table.getRowValue(row, 'exp_gnwp_eur_new_total'),

                nlrRenewed: table.getRowValue(row, 'nlr_renewed'),
                nlrRenewedUwy: table.getRowValue(row, 'nlr_uwy_renewed'),
                nlrRemaining: table.getRowValue(row, 'nlr_remaining'),
                nlrFinalRemaining: table.getRowValue(row, 'nlr_final_remaining'),
                nlrNewTotal: table.getRowValue(row, 'nlr_new_total'),

                rateChangeRenewed: table.getRowValue(row, 'rate_change_renewed'),
                rateChangeRenewedUwy: table.getRowValue(row, 'rate_change_uwy_renewed'),
                rateChangeRemaining: table.getRowValue(row, 'rate_change_remaining'),
                rateChangeFinalRemaining: table.getRowValue(row, 'rate_change_remaining'), // 'final' = same as 'remaining'
                rateChangeNewTotal: table.getRowValue(row, 'rate_change_new_total'),

                retentionRateRenewed: table.getRowValue(row, 'retention_rate_renewed'),
                retentionRateRenewedUwy: table.getRowValue(row, 'retention_rate_uwy_renewed'),
                retentionRateRemaining: table.getRowValue(row, 'retention_rate_remaining'),
                retentionRateFinalRemaining: table.getRowValue(row, 'retention_rate_remaining'), // 'final' = same as 'remaining'
                retentionRateNewTotal: table.getRowValue(row, 'retention_rate_new_total'),

                // --- this is input from the users to refine the outstanding accounts in the renewal
                targetRateChangeRemaining: table.getRowValue(row, 'targetRateChangeRemaining'),
                targetRetentionRateRemaining: table.getRowValue(row, 'targetRetentionRateRemaining'),

                fyGnep: table.getRowValue(row, 'fy_gnep'),
                fyGnwpEarnedInUwy: table.getRowValue(row, 'fy_gnwp_earned_in_uwy_prior_basis'),
                fyNlr: table.getRowValue(row, 'fy_nlr'),
                fyPlr: table.getRowValue(row, 'fy_plr'),
                fyEarnedRateChange: table.getRowValue(row, 'fy_earned_rate_change'),
                fyPlrDelta: table.getRowValue(row, 'fy_plr_delta'),

                llCount: table.getRowValue(row, 'll_count'),
                llPct: table.getRowValue(row, 'll_pct'),
                llGnwpEur: table.getRowValue(row, 'll_gnwp_eur'),
                llExpectedLlPerYear: table.getRowValue(row, 'll_expected_no_ll_per_year'),
                llIntensity: table.getRowValue(row, 'll_intensity'),

                llGnwpEurRenewed: table.getRowValue(row, 'll_renewal_gnwp_eur'),
                llExpectedLlPerYearRenewed: table.getRowValue(row, 'll_renewal_expected_no_ll_per_year'),
                llIntensityRenewed: table.getRowValue(row, 'll_renewal_intensity'),
                llFrequencyReduction: table.getRowValue(row, 'll_renewal_freq_red'),
                llPrunedInRenewal: table.getRowValue(row, 'll_renewal_pruned'),

            })
        );
        this.uwySummary.set(uwySummary);
    }

    private readTieringStats(evalResponse: FormulaEvaluateResponse): TieringStats[] {
        const tables = evalResponse.tables!;
        const tableRef = PortfolioSegmentWorkbook.getTableByName(tables, AppConstants.PYFORMULA_TIERING_STATS_TABLE_NAME);
        const table = new FormulaTableAccessor(tableRef, 'index');

        const tieringStats = ['calculated', 'final', 'prev'].flatMap(type => {
            return tableRef.data.map(row => ({
                typeKey: type,
                categoryKey: table.getRowValue(row, 'index') as string,

                count: table.getRowValue(row, `tiering_stats_${type}_policy_count`),
                countPercent: table.getRowValue(row, `tiering_stats_${type}_policy_count_pct`),
                gnwp: table.getRowValue(row, `tiering_stats_${type}_gnwp`),
                gnwpPct: table.getRowValue(row, `tiering_stats_${type}_gnwp_pct`),
            }) as TieringStats);
        });

        this.tieringStats.set(tieringStats);
        return tieringStats;
    }

    private readChecksSummary(evalResponse: FormulaEvaluateResponse): void {
        const tables = evalResponse.tables!;
        const checksDictionaryTable = PortfolioSegmentWorkbook.getTableByName(tables, AppConstants.PYFORMULA_ACCOUNT_CHECKS_DICTIONARY_TABLE_NAME);
        const checksTable = PortfolioSegmentWorkbook.getTableByName(tables, AppConstants.PYFORMULA_ACCOUNT_CHECKS_TABLE_NAME);

        const checksDictionaryAccessor = new FormulaTableAccessor(checksDictionaryTable, 'id');
        const checksAccessor = new FormulaTableAccessor(checksTable, '');

        const Dictionary = checksDictionaryTable.data.map(it => {
            const checkId = checksDictionaryAccessor.getRowValue(it, 'id') as string;
            const count = ArrayUtils.countIf(checksTable.data, row => checksAccessor.getRowValue(row, checkId) != null);

            return {
                id: checkId,
                name: checksDictionaryAccessor.getRowValue(it, 'name') as string,
                severity: checksDictionaryAccessor.getRowValue(it, 'severity') as CheckSeverity,
                description: checksDictionaryAccessor.getRowValue(it, 'name') as string,
                count: count
            } as PfValidationCheckItem
        });

        this.checksSummary.set(Dictionary);
    }

    summaryToTargets(tiers: ClassificationTierTargetWithName[]): Target[] {
        const targets: Target[] = [];
        const summary = this.uwySummary();

        function mapTargets(block: (s: PfTargetUwySummaryRow) => Target){
            summary.forEach(s => targets.push(block(s)));
        }

        mapTargets(s => ({
            typeKey: 'original', categoryKey: s.bucket,
            gnwp: s.renewalGnwpEur, nlr: s.renewalNlr, rateChange: s.targetRateChange, retentionRate: s.targetRetentionRate
        }));

        mapTargets(s => ({
            typeKey: 'renewed', categoryKey: s.bucket,
            gnwp: s.gnwpEurRenewed, nlr: s.nlrRenewed, rateChange: s.rateChangeRenewed, retentionRate: s.retentionRateRenewed
        }));

        mapTargets(s => ({
            typeKey: 'renewed_uwy', categoryKey: s.bucket,
            gnwp: s.gnwpEurRenewedUwy, nlr: s.nlrRenewedUwy, rateChange: s.rateChangeRenewedUwy, retentionRate: s.retentionRateRenewedUwy
        }));

        mapTargets(s => ({
            typeKey: 'suggested_remaining', categoryKey: s.bucket,
            gnwp: s.gnwpEurRemaining, nlr: s.nlrRemaining, rateChange: s.rateChangeRemaining, retentionRate: s.retentionRateRemaining
        }));

        mapTargets(s => ({
            typeKey: 'final_remaining', categoryKey: s.bucket,
            gnwp: s.gnwpEurFinalRemaining, nlr: s.nlrFinalRemaining, rateChange: s.rateChangeFinalRemaining, retentionRate: s.retentionRateFinalRemaining
        }));

        mapTargets(s => ({
            typeKey: 'new_targets', categoryKey: s.bucket,
            gnwp: s.gnwpEurNewTotal, nlr: s.nlrNewTotal, rateChange: s.rateChangeNewTotal, retentionRate: s.retentionRateNewTotal
        }));

        // From table "Dashboard / Tracking" => "Additional Info" (second table)
        mapTargets(s => ({
            typeKey: 'info2_renewed_portfolio_pre', categoryKey: s.bucket,
            gnwp: s.expGnwpEurRenewed, nlr: s.expNlrRenewed
        }));

        // From table "PF Reports / Targets" => "UW Year Summary", last block
        mapTargets(s => ({
            typeKey: 'new_portfolio', categoryKey: s.bucket,
            gnwp: s.renewalGnwpEur, nlr: s.renewalNlr,
        }));

        return targets;
    }

    /** Convert an attribute by its name to a JSON data column definition using the data dictionary. */
    private static accountAttrToColumn(attr: string): FormulaEvaluateTableColumnNonNullJsonType {
        if (/percentile|adequacy|_/.test(attr)) {
            return { name: attr, jsonType: JsonDataType.Number };
        } else {
            return {name: attr, jsonType: DataDictionaryService.getAccountDataAttributeJsonType(attr) };
        }
    }

    static kpisToPercentileAndWeightAttributes(kpiAttributes: string[]) {
        return kpiAttributes.flatMap(attr => [`${attr}_percentile`, `${attr}_wpercentile`]);
    }

    calculateStatistics(): PortfolioSegmentStatistics {
        return AccountSegmentStatisticsCalculator.fromAccounts(this.pfSegDataCtx.getAccountData());
    }

    static mapCalculatedFields(accounts: (AccountData | Partial<AccountData>)[] | undefined):  AccountData[] {
        if (!accounts  || accounts.length === 0) return [];

        return accounts.map(account => {
            const mappedAccount: AccountData = {};
            for (const attr of PortfolioSegmentWorkbook.AccountColumnsToReceive) {
                const n = attr.name as keyof AccountData;
                mappedAccount[n] = JsonHelper.convertValueToJson(account[n], attr.jsonType as JsonDataType);
            }
            mappedAccount['id'] = account.id;
            mappedAccount['version'] = account.version;
            mappedAccount['metadataChecks'] = account.metadataChecks;
            return mappedAccount;
        });
    }
}
